import type { LooperEngine } from "../LooperEngine";
import type { Cell, Hit } from "../types";
import { registerDropZone } from "../../shared/dragDrop";

// The visible MIDI window auto-fits to the cell's hits (+/- a buffer) so
// notes are tall and readable instead of squashed into a fixed 4-octave strip.
// Hard clamps span the whole MIDI range so very low (sub-bass) or very high
// hits remain visible — the auto-fit picks the window around them.
const HARD_LO = 0;             // MIDI 0  (~8 Hz)
const HARD_HI = 127;           // MIDI 127 (~12.5 kHz)
const MIN_RANGE = 24;          // never zoom in tighter than 2 octaves
const BUFFER_SEMI = 3;         // padding around the highest/lowest hit
const DEFAULT_LO = 48;         // C3 — used for an empty cell
const DEFAULT_HI = 72;         // C5

// Snap divisions per beat. 4 = 1/16 (default), 2 = 1/8, 1 = 1/4, 8 = 1/32, 0 = free.
type SnapDiv = 0 | 1 | 2 | 4 | 8;
const SNAP_OPTIONS: { label: string; value: SnapDiv }[] = [
  { label: "1/4",  value: 1 },
  { label: "1/8",  value: 2 },
  { label: "1/16", value: 4 },
  { label: "1/32", value: 8 },
  { label: "free", value: 0 },
];

export interface NoteEditorOptions {
  engine: LooperEngine;
  cell: Cell;
  onChange: () => void;
  /** Register a dropped palette slot's sound, returning its id. */
  resolveDropSound?: (paletteIndex: number) => string | null;
}

// Distinct, stable color per sound id, so a box mixing several sounds is legible.
function hueFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return `hsl(${h}, 70%, 64%)`;
}

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const noteName = (midi: number): string => `${NOTE_NAMES[midi % 12]}${Math.floor(midi / 12) - 1}`;
// Format a beat count compactly: 2 · 2.5 · 0.25 …
const fmtBeats = (b: number): string => (Math.round(b * 100) / 100).toString();

// Right-edge grab zone of a note, in px — drag it to change the length (works
// on touch, where the scroll-to-resize wheel doesn't exist).
const RESIZE_PX = 10;

// A large popup editor for one box's notes.
//  - Click a note: select; drag: move (with grid snap).
//  - Click empty: add a new note (snapped) and drag-place it.
//  - Shift+click a note: toggle in the multi-selection.
//  - Shift+drag from empty: box-select notes. Selected notes move as a group.
//  - Scroll over a note: change its duration.
//  - Right-click a note: delete. Delete key: delete all selected.
//  - Arrows nudge all selected. Esc / ✕ / click-away close.
export function openNoteEditor(opts: NoteEditorOptions): void {
  const { engine, cell, onChange } = opts;
  if (document.querySelector(".editor-backdrop")) return; // one editor at a time

  const backdrop = document.createElement("div");
  backdrop.className = "editor-backdrop";
  const panel = document.createElement("div");
  panel.className = "editor-panel";
  const head = document.createElement("div");
  head.className = "editor-head";
  const title = document.createElement("span");
  title.textContent = `Edit — ${cell.name}`;

  // Snap selector (right side of the head, before the close button)
  const snapWrap = document.createElement("label");
  snapWrap.className = "editor-snap";
  snapWrap.textContent = "snap ";
  const snapSel = document.createElement("select");
  for (const o of SNAP_OPTIONS) {
    const opt = document.createElement("option");
    opt.value = String(o.value);
    opt.textContent = o.label;
    if (o.value === 4) opt.selected = true; // default 1/16
    snapSel.append(opt);
  }
  snapWrap.append(snapSel);

  const closeBtn = document.createElement("button");
  closeBtn.className = "editor-close";
  closeBtn.textContent = "✕";

  // Clear (moved here from the box card — the editor is where you look at notes).
  const clearBtn = document.createElement("button");
  clearBtn.className = "editor-clear";
  clearBtn.textContent = "Clear";
  clearBtn.title = "Delete all notes in this box";

  const headRight = document.createElement("div");
  headRight.className = "editor-head-right";
  headRight.append(snapWrap, clearBtn, closeBtn);
  head.append(title, headRight);

  const canvas = document.createElement("canvas");
  canvas.className = "editor-canvas";
  const hint = document.createElement("p");
  hint.className = "editor-hint";
  hint.textContent =
    "drag = move (snaps to grid) · drag right edge = length · click = add · " +
    "Shift+click = multi-select · Shift+drag empty = box-select · scroll = length · " +
    "Alt+scroll = velocity · right-click = delete · arrows nudge · Space = play/stop · Esc = close";
  panel.append(head, canvas, hint);
  backdrop.append(panel);
  document.body.append(backdrop);
  document.body.dataset.editor = "open";

  // ---- Visible MIDI window (auto-fitted) ----
  let LOW = DEFAULT_LO;
  let HIGH = DEFAULT_HI;
  function fitRangeToHits(): void {
    if (cell.hits.length === 0) {
      LOW = DEFAULT_LO;
      HIGH = DEFAULT_HI;
    } else {
      let mn = 127, mx = 0;
      for (const h of cell.hits) {
        if (h.midi < mn) mn = h.midi;
        if (h.midi > mx) mx = h.midi;
      }
      LOW = Math.max(HARD_LO, mn - BUFFER_SEMI);
      HIGH = Math.min(HARD_HI, mx + BUFFER_SEMI);
    }
    // Centre-expand if the window is too small.
    if (HIGH - LOW < MIN_RANGE) {
      const grow = MIN_RANGE - (HIGH - LOW);
      LOW = Math.max(HARD_LO, LOW - Math.floor(grow / 2));
      HIGH = Math.min(HARD_HI, LOW + MIN_RANGE);
    }
  }
  fitRangeToHits();

  // ---- State ----
  const selected = new Set<Hit>();
  let dragging: {
    anchor: Hit;
    grabT: number;     // pointer time when grab started, minus anchor.time
    grabMidi: number;  // pointer midi when grab started, minus anchor.midi
    deltas: Map<Hit, { dt: number; dmidi: number }>; // each selected's offset from anchor
  } | null = null;
  let boxSel: { x0: number; y0: number; x1: number; y1: number } | null = null;
  let resizing: Hit | null = null;      // dragging a note's right edge = length
  let infoHit: Hit | null = null;       // note whose readout is showing
  let infoUntil = 0;                    // keep the readout up briefly after a change
  let snapDiv: SnapDiv = 4; // 1/16 default
  let activePatchId: string | null = null;
  let raf = 0;

  snapSel.addEventListener("change", () => {
    snapDiv = Number(snapSel.value) as SnapDiv;
  });

  // ---- Coords ----
  const bpm = (): number => engine.currentProject.bpm;
  const loopPre = (): number => cell.lengthBeats * (60 / bpm());
  // A buffer of extra beats drawn after the loop end, so a note whose tail runs
  // past the repeat stays visible (and you can see it rings into the next cycle).
  const bufferBeats = (): number => Math.min(4, Math.max(1, Math.round(cell.lengthBeats * 0.15)));
  const visibleDur = (): number => (cell.lengthBeats + bufferBeats()) * (60 / bpm());

  const rectOf = () => canvas.getBoundingClientRect();
  const xToTime = (clientX: number): number => {
    const r = rectOf();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * visibleDur();
  };
  const yToMidi = (clientY: number): number => {
    const r = rectOf();
    return Math.round(HIGH - Math.min(1, Math.max(0, (clientY - r.top) / r.height)) * (HIGH - LOW));
  };
  const timeToX = (time: number, w: number): number => {
    const vd = visibleDur();
    return vd > 0 ? (time / vd) * w : 0;
  };
  const midiToY = (midi: number, h: number): number => ((HIGH - midi) / (HIGH - LOW)) * h;

  // ---- Snap ----
  // Snap a time-in-seconds to the chosen grid (relative to the loop start).
  function snapTime(t: number): number {
    if (snapDiv === 0) return t;
    const step = (60 / bpm()) / snapDiv;
    return Math.round(t / step) * step;
  }

  function noteAt(clientX: number, clientY: number): Hit | null {
    const r = rectOf();
    const x = clientX - r.left;
    const y = clientY - r.top;
    const laneH = Math.max(4, r.height / (HIGH - LOW));
    for (let i = cell.hits.length - 1; i >= 0; i--) {
      const hit = cell.hits[i];
      const nx = timeToX(hit.time, r.width);
      const nw = Math.max(6, timeToX(hit.dur, r.width));
      const ny = midiToY(hit.midi, r.height);
      if (x >= nx - 3 && x <= nx + nw + 3 && y >= ny - laneH / 2 - 3 && y <= ny + laneH / 2 + 3) return hit;
    }
    return null;
  }

  function defaultPatchId(): string {
    return activePatchId || engine.currentPatchId || cell.hits[0]?.patchId || engine.currentProject.palette[0]?.id || "";
  }

  function removeHit(hit: Hit): void {
    const i = cell.hits.indexOf(hit);
    if (i >= 0) cell.hits.splice(i, 1);
    selected.delete(hit);
  }

  // Begin a drag of every selected note, with their offsets recorded relative
  // to the anchor (the note the pointer grabbed).
  function beginDrag(anchor: Hit, e: PointerEvent): void {
    const t = xToTime(e.clientX);
    const m = yToMidi(e.clientY);
    const deltas = new Map<Hit, { dt: number; dmidi: number }>();
    for (const h of selected) deltas.set(h, { dt: h.time - anchor.time, dmidi: h.midi - anchor.midi });
    dragging = { anchor, grabT: t - anchor.time, grabMidi: m - anchor.midi, deltas };
  }

  // ---- Pointer ----
  // Is the pointer over a note's right edge (the resize handle)?
  function onResizeEdge(hit: Hit, clientX: number): boolean {
    const r = rectOf();
    const nx = timeToX(hit.time, r.width);
    const nw = Math.max(6, timeToX(hit.dur, r.width));
    return clientX - r.left > nx + nw - RESIZE_PX && nw >= RESIZE_PX + 4;
  }

  canvas.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    const hit = noteAt(e.clientX, e.clientY);
    if (hit) {
      if (e.shiftKey) {
        if (selected.has(hit)) selected.delete(hit);
        else selected.add(hit);
      } else if (!selected.has(hit)) {
        selected.clear();
        selected.add(hit);
      }
      // Grab the right edge → change length instead of moving.
      if (!e.shiftKey && onResizeEdge(hit, e.clientX)) {
        resizing = hit;
        infoHit = hit;
        return;
      }
      if (selected.has(hit)) {
        beginDrag(hit, e);
        infoHit = hit;
      }
      return;
    }
    // Empty area:
    if (e.shiftKey) {
      // box-select (additive — keeps current selection until pointerup)
      boxSel = { x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY };
      return;
    }
    // Add a new note (snapped) and drag it.
    selected.clear();
    const dur = Math.max(0.08, (60 / bpm()) * 0.5);
    const newHit: Hit = {
      patchId: defaultPatchId(),
      time: Math.max(0, Math.min(loopPre() - 0.01, snapTime(xToTime(e.clientX)))),
      dur,
      midi: yToMidi(e.clientY),
      gain: 0.9,
    };
    cell.hits.push(newHit);
    selected.add(newHit);
    beginDrag(newHit, e);
    infoHit = newHit;
    onChange();
  });

  canvas.addEventListener("pointermove", (e) => {
    if (boxSel) {
      boxSel.x1 = e.clientX;
      boxSel.y1 = e.clientY;
      return;
    }
    if (resizing) {
      // Snap the note END to the grid (free = exact); minimum one 32nd.
      const minDur = (60 / bpm()) / 8;
      resizing.dur = Math.max(minDur, snapTime(xToTime(e.clientX)) - resizing.time);
      return;
    }
    if (!dragging) {
      // Idle hover: show the resize cursor over a note's right edge.
      const hover = noteAt(e.clientX, e.clientY);
      canvas.style.cursor = hover && onResizeEdge(hover, e.clientX) ? "ew-resize" : "crosshair";
      return;
    }
    // Anchor target time/midi from pointer.
    const targetT = snapTime(Math.max(0, Math.min(loopPre() - 0.01, xToTime(e.clientX) - dragging.grabT)));
    const targetM = Math.max(LOW, Math.min(HIGH, yToMidi(e.clientY) - dragging.grabMidi));
    for (const [h, d] of dragging.deltas) {
      h.time = Math.max(0, Math.min(loopPre() - 0.01, targetT + d.dt));
      h.midi = Math.max(LOW, Math.min(HIGH, targetM + d.dmidi));
    }
  });

  const endDrag = (): void => {
    if (boxSel) {
      // Commit box-select: any note whose box overlaps the rectangle joins selected.
      const r = rectOf();
      const xLo = Math.min(boxSel.x0, boxSel.x1) - r.left;
      const xHi = Math.max(boxSel.x0, boxSel.x1) - r.left;
      const yLo = Math.min(boxSel.y0, boxSel.y1) - r.top;
      const yHi = Math.max(boxSel.y0, boxSel.y1) - r.top;
      const laneH = Math.max(4, r.height / (HIGH - LOW));
      for (const hit of cell.hits) {
        const nx = timeToX(hit.time, r.width);
        const nw = Math.max(6, timeToX(hit.dur, r.width));
        const ny = midiToY(hit.midi, r.height);
        const nyLo = ny - laneH / 2;
        const nyHi = ny + laneH / 2;
        if (nx + nw >= xLo && nx <= xHi && nyHi >= yLo && nyLo <= yHi) selected.add(hit);
      }
      boxSel = null;
    }
    if (resizing) {
      resizing = null;
      infoUntil = performance.now() + 900;
      onChange();
    }
    if (dragging) {
      dragging = null;
      infoUntil = performance.now() + 900;
      onChange();
    }
  };
  canvas.addEventListener("pointerup", endDrag);
  canvas.addEventListener("pointercancel", endDrag);
  canvas.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    const hit = noteAt(e.clientX, e.clientY);
    if (hit) {
      removeHit(hit);
      onChange();
    }
  });

  // Drop a sample tile here (pointer drag) to assign a sound: onto a note
  // repaints just that note; onto empty space sets the sound for notes you add
  // next. The top bar sits above the editor backdrop so tiles stay reachable.
  const dropCleanup = registerDropZone({
    el: canvas,
    overClass: "drag-over",
    accepts: (d) => d.kind === "slot",
    onDrop: (d, clientX, clientY) => {
      if (!opts.resolveDropSound) return;
      const id = opts.resolveDropSound(Number(d.id));
      if (!id) return;
      const hit = noteAt(clientX, clientY);
      if (hit) {
        hit.patchId = id;
        selected.clear();
        selected.add(hit);
      } else {
        activePatchId = id;
      }
      onChange();
    },
  });

  // Scroll over a note to change its duration (scroll up = longer).
  canvas.addEventListener(
    "wheel",
    (e) => {
      const hit = noteAt(e.clientX, e.clientY);
      if (!hit) return;
      e.preventDefault();
      if (e.altKey) {
        // Alt+scroll = velocity (per-note loudness). Parallels scroll = length.
        hit.gain = Math.max(0.05, Math.min(1, (hit.gain ?? 0.9) - Math.sign(e.deltaY) * 0.08));
      } else {
        const step = (60 / bpm()) / 8; // a 32nd note per notch
        const maxDur = Math.max(loopPre(), 4);
        hit.dur = Math.max(0.03, Math.min(maxDur, hit.dur - Math.sign(e.deltaY) * step));
      }
      infoHit = hit;
      infoUntil = performance.now() + 900;
      onChange();
    },
    { passive: false },
  );

  clearBtn.addEventListener("click", () => {
    cell.hits.length = 0;
    selected.clear();
    onChange();
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      close();
      return;
    }
    if (selected.size === 0) return;
    const step = (60 / bpm()) / Math.max(1, snapDiv || 4);
    if (e.key === "ArrowLeft") {
      for (const h of selected) h.time = Math.max(0, h.time - step);
    } else if (e.key === "ArrowRight") {
      for (const h of selected) h.time = Math.min(loopPre() - 0.01, h.time + step);
    } else if (e.key === "ArrowUp") {
      for (const h of selected) h.midi = Math.min(HIGH, h.midi + 1);
    } else if (e.key === "ArrowDown") {
      for (const h of selected) h.midi = Math.max(LOW, h.midi - 1);
    } else if (e.key === "Delete" || e.key === "Backspace") {
      for (const h of [...selected]) removeHit(h);
    } else return;
    e.preventDefault();
    onChange();
  }
  window.addEventListener("keydown", onKey);

  function close(): void {
    cancelAnimationFrame(raf);
    window.removeEventListener("keydown", onKey);
    dropCleanup();
    backdrop.remove();
    delete document.body.dataset.editor;
  }
  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("pointerdown", (e) => {
    if (e.target === backdrop) close();
  });

  function draw(): void {
    raf = requestAnimationFrame(draw);
    const dpr = window.devicePixelRatio || 1;
    const r = rectOf();
    const w = r.width;
    const h = r.height;
    if (w === 0 || h === 0) return;
    if (canvas.width !== Math.floor(w * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // ---- Grid + loop frame ----
    const lb = cell.lengthBeats;
    const vb = lb + bufferBeats();      // beats drawn (loop + buffer)
    const beatX = (b: number): number => (b / vb) * w;
    const frameX = beatX(lb);           // where the loop repeats
    const vline = (x: number): void => {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    };

    // Buffer zone after the loop end — a note tail reaching here rings over into
    // the next cycle (keeps playing past the repeat).
    ctx.fillStyle = "rgba(0,0,0,0.26)";
    ctx.fillRect(frameX, 0, w - frameX, h);

    // Subdivision grid (1/16 etc.) — brighter than before, behind everything.
    if (snapDiv > 0) {
      ctx.strokeStyle = "rgba(255,255,255,0.07)";
      ctx.lineWidth = 1;
      const totalSubs = vb * snapDiv;
      for (let i = 1; i < totalSubs; i++) {
        if (i % snapDiv === 0) continue; // beat lines drawn brighter below
        vline((i / totalSubs) * w);
      }
    }
    // Beat lines — bright inside the loop, faint in the buffer.
    ctx.lineWidth = 1;
    for (let b = 0; b <= vb; b++) {
      ctx.strokeStyle = b <= lb ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.08)";
      vline(beatX(b));
    }
    // Loop-end marker (the repeat line).
    ctx.strokeStyle = "rgba(0,191,255,0.75)";
    ctx.lineWidth = 2;
    vline(frameX);
    ctx.lineWidth = 1;

    // Octave guides — on every C, with its label (C3, C4 …) in the left gutter.
    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.font = "600 10px system-ui, -apple-system, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    for (let m = Math.ceil(LOW / 12) * 12; m <= HIGH; m += 12) {
      const y = midiToY(m, h);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
      ctx.fillText(noteName(m), 4, y - 3);
    }

    // If the box mixes several sounds, tint notes per sound so they're legible.
    const multiSound = new Set(cell.hits.map((x) => x.patchId)).size > 1;
    const laneH = Math.max(4, h / Math.max(1, HIGH - LOW));
    for (const hit of cell.hits) {
      const nx = timeToX(hit.time, w);
      const nw = Math.max(6, timeToX(hit.dur, w));
      const ny = midiToY(hit.midi, h);
      const isSel = selected.has(hit);
      // Opacity carries velocity (hit.gain) so loud/soft notes read at a glance.
      const noteAlpha = isSel ? 1 : 0.4 + Math.min(1, Math.max(0, hit.gain)) * 0.55;
      ctx.fillStyle = isSel ? "#ffffff" : multiSound ? hueFor(hit.patchId) : cell.color;
      ctx.globalAlpha = noteAlpha;
      ctx.fillRect(nx, ny - laneH / 2, nw, laneH);
      // Tail running past the loop end → ghost at the start, where it actually
      // sounds on the next pass.
      const overflow = hit.time + hit.dur - loopPre();
      if (overflow > 0) {
        const gw = Math.min(timeToX(overflow, w), frameX);
        ctx.globalAlpha = 0.28;
        ctx.fillRect(0, ny - laneH / 2, gw, laneH);
        ctx.globalAlpha = noteAlpha;
      }
      if (isSel) {
        ctx.strokeStyle = "#00bfff";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(nx - 1, ny - laneH / 2 - 1, nw + 2, laneH + 2);
      }
    }
    ctx.globalAlpha = 1;

    // Box-select rectangle.
    if (boxSel) {
      const xLo = Math.min(boxSel.x0, boxSel.x1) - r.left;
      const xHi = Math.max(boxSel.x0, boxSel.x1) - r.left;
      const yLo = Math.min(boxSel.y0, boxSel.y1) - r.top;
      const yHi = Math.max(boxSel.y0, boxSel.y1) - r.top;
      ctx.fillStyle = "rgba(0, 191, 255, 0.10)";
      ctx.strokeStyle = "rgba(0, 191, 255, 0.8)";
      ctx.lineWidth = 1;
      ctx.fillRect(xLo, yLo, xHi - xLo, yHi - yLo);
      ctx.strokeRect(xLo + 0.5, yLo + 0.5, xHi - xLo - 1, yHi - yLo - 1);
    }

    // Live note readout — while dragging/resizing (and briefly after): note
    // name, start position and length in beats, velocity. The "view of the
    // note" that makes snapping/length concrete.
    const showInfo = dragging?.anchor ?? resizing ?? (performance.now() < infoUntil ? infoHit : null);
    if (showInfo && cell.hits.includes(showInfo)) {
      const beat = 60 / bpm();
      const text =
        `${noteName(showInfo.midi)} · @${fmtBeats(showInfo.time / beat)}b` +
        ` · len ${fmtBeats(showInfo.dur / beat)}b · v${(Math.round((showInfo.gain ?? 1) * 100) / 100).toString()}`;
      // Square readout — the rest of the UI is flat, so no rounded corner here.
      ctx.font = "700 13px ui-monospace, SFMono-Regular, Menlo, monospace";
      const tw = ctx.measureText(text).width;
      ctx.fillStyle = "rgba(0,0,0,0.82)";
      ctx.fillRect(8, 8, tw + 20, 26);
      ctx.strokeStyle = "rgba(0,191,255,0.5)";
      ctx.strokeRect(8.5, 8.5, tw + 19, 25);
      ctx.fillStyle = "#e9eff6";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(text, 18, 21 + 1);
    }

    // Playhead — sweeps the loop frame only (it wraps at the loop-end marker).
    const phase = engine.getCellPhase(cell);
    if (phase > 0) {
      ctx.strokeStyle = "rgba(255,255,255,0.5)";
      ctx.beginPath();
      ctx.moveTo(phase * frameX, 0);
      ctx.lineTo(phase * frameX, h);
      ctx.stroke();
    }
  }
  draw();
}
